import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { LEAD_BASED_PAINT, STUDENT_CODE_TYPES } from '@shared/utils/app-static-data';
import { IIndividualFilter } from 'app/models/individuals/individual-filter';
import { IIndividual } from 'app/models/individuals/individual';
import { CommonService } from 'app/services/common/common.service';
import { SharedService } from 'app/services/core/shared.service';
import { IndividualService } from 'app/services/individuals/individual.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-individual-list',
  templateUrl: './individual-list.component.html',
  styleUrls: ['./individual-list.component.scss']
})
export class IndividualListComponent implements OnInit {
  public isMobile = false;
  public loading = true;
  public pageSize = 25;
  public uiData = LEAD_BASED_PAINT;
  public filterString = '';
  public filteredActive = new FormControl('Active');
  public expirationStartDate = new FormControl(null);
  public expirationEndDate = new FormControl(null);
  public disciplineTypes = new FormControl([]);
  public individuals: IIndividual[] = [];
  public dataSource = new MatTableDataSource<IIndividual>([]);
  public displayedColumns = ['firstName', 'lastName', 'status', 'publicListing', 'affiliations', 'certificates', 'actions'];
  public individualTable = {
    layout: {
      columns: ['firstName', 'lastName', 'status', 'publicListing', 'affiliations', 'certificates'],
      container: [
        { displayName: 'First Name', columnName: 'firstName', type: 'string', size: '14' },
        { displayName: 'Last Name', columnName: 'lastName', type: 'string', size: '14' },
        { displayName: 'Status', columnName: 'status', type: 'status', size: '10' },
        { displayName: 'Listing', columnName: 'publicListing', type: 'string', size: '10' },
        { displayName: 'Affiliations', columnName: 'affiliations', type: 'string', size: '20' },
        { displayName: 'Certificates', columnName: 'certificates', type: 'string', size: '22' },
      ],
      actionColumnSize: '10',
    },
  };

  @ViewChild('TablePaginator') paginator!: MatPaginator;
  @ViewChild('TableSort') sort!: MatSort;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private commonService: CommonService,
    private individualService: IndividualService,
    public sharedService: SharedService,
    private toastr: ToastrService,
  ) {
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe((result) => {
      this.isMobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.loadFilters();
    this.loadIndividuals();
  }

  loadFilters(): void {
    this.filterString = this.commonService.individualManagementFilter.filterString;
    this.filteredActive = this.commonService.individualManagementFilter.filteredActive;
    this.expirationStartDate = this.commonService.individualManagementFilter.expirationStartDate;
    this.expirationEndDate = this.commonService.individualManagementFilter.expirationEndDate;
    this.disciplineTypes = this.commonService.individualManagementFilter.disciplineTypes;
  }

  loadIndividuals(): void {
    this.loading = true;
    this.persistFilters();

    this.individualService.getUserIndividuals(this.getFilter()).subscribe({
      next: (individuals) => {
        this.individuals = individuals;
        this.dataSource = new MatTableDataSource<IIndividual>(individuals);
        setTimeout(() => {
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
        });
        this.loading = false;
      },
      error: (error) => {
        this.toastr.error('Error loading Individuals: ' + error.message);
        this.loading = false;
      }
    });
  }

  clearFilters(): void {
    this.filterString = '';
    this.filteredActive.setValue('Active');
    this.expirationStartDate.reset();
    this.expirationEndDate.reset();
    this.disciplineTypes.setValue([]);
    this.loadIndividuals();
  }

  openIndividualEdit(individual: IIndividual): void {
    this.router.navigate(['/individual-edit'], {
      queryParams: { personId: individual.id },
    });
  }

  openApplications(): void {
    this.router.navigate(['/individuals/applications']);
  }

  getDisciplineValues(): string[] {
    return Object.values(STUDENT_CODE_TYPES).sort();
  }

  getStatus(individual: IIndividual): string {
    return individual.inactive ? 'Inactive' : 'Active';
  }

  getPublicListing(individual: IIndividual): string {
    return individual.response === 'Yes' ? 'Public' : 'Private';
  }

  getAffiliationSummary(individual: IIndividual): string {
    const filteredAffiliations = (individual.affiliations || [])
      .map((affiliation) => this.cleanAffiliationLabel(affiliation))
      .filter((affiliation) => this.isAtpOrFirmAffiliation(affiliation))
      .filter((affiliation, index, list) => list.findIndex((value) => value.toLowerCase() === affiliation.toLowerCase()) === index);

    return filteredAffiliations.length ? filteredAffiliations.join(', ') : 'Not provided';
  }

  getColumnText(individual: IIndividual, columnName: string): string {
    switch (columnName) {
      case 'status':
        return this.getStatus(individual);
      case 'publicListing':
        return this.getPublicListing(individual);
      case 'affiliations':
        return this.getAffiliationSummary(individual);
      case 'certificates':
        return this.individualService.getCertificateSummary(individual);
      default: {
        const value = individual[columnName as keyof IIndividual];
        return value === null || value === undefined || value === '' ? 'Not provided' : value.toString();
      }
    }
  }

  statusColor(status: string): string {
    return this.sharedService.getStatusColor(status);
  }

  private cleanAffiliationLabel(affiliation: string): string {
    return (affiliation || '')
      .replace(/\bcourse\s*ids?\s*[:#-]?\s*\d+(?:\s*,\s*\d+)*/ig, '')
      .replace(/[-,:;|/]\s*$/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  private isAtpOrFirmAffiliation(affiliation: string): boolean {
    if (!affiliation) {
      return false;
    }

    const normalized = affiliation.toLowerCase();
    return normalized.includes('atp') || normalized.includes('firm');
  }

  private persistFilters(): void {
    this.commonService.individualManagementFilter.filterString = this.filterString;
    this.commonService.individualManagementFilter.filteredActive = this.filteredActive;
    this.commonService.individualManagementFilter.expirationStartDate = this.expirationStartDate;
    this.commonService.individualManagementFilter.expirationEndDate = this.expirationEndDate;
    this.commonService.individualManagementFilter.disciplineTypes = this.disciplineTypes;
  }

  private getFilter(): IIndividualFilter {
    return {
      filterString: this.filterString,
      filterStatus: this.filteredActive.value,
      expirationStartDate: this.expirationStartDate.value ? new Date(this.expirationStartDate.value).toISOString() : null,
      expirationEndDate: this.expirationEndDate.value ? new Date(this.expirationEndDate.value).toISOString() : null,
      disciplineTypes: this.disciplineTypes.value || [],
    };
  }
}